import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DemoInquiryDialog } from '@/components/marketing/DemoInquiryDialog';
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from 'framer-motion';

const ease = [0.16, 1, 0.3, 1] as [number, number, number, number];

const navLinks = [
  { label: 'Platform', to: '/features' },
  { label: 'Solutions', to: '/solutions/private-equity-ai' },
  { label: 'Use Cases', to: '/use-cases' },
  { label: 'Resources', to: '/resources/private-equity-ai-buyers-guide' },
  { label: 'Pricing', to: '/pricing' },
];

export function MarketingNav() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 20));
  const isHome = pathname === '/';

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeMenu = (restoreFocus = false) => {
    setMenuOpen(false);
    if (restoreFocus) requestAnimationFrame(() => menuTriggerRef.current?.focus());
  };

  return (
    <motion.nav
      className={`marketing-nav ${isHome ? 'marketing-nav--home' : ''} ${scrolled ? 'marketing-nav--scrolled' : ''} fixed top-0 w-full z-50 transition-[backdrop-filter] duration-300`}
      style={{
        backdropFilter: scrolled ? 'blur(18px) saturate(120%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(18px) saturate(120%)' : 'none',
      }}
    >
      <div className="marketing-nav__inner mx-auto px-4 sm:px-5 md:px-8 flex items-center justify-between gap-2 md:gap-4">
        <Link to="/" className="marketing-nav__logo flex items-center shrink-0" aria-label="ReturnCatalyst home">
          <span className="marketing-wordmark__mark" aria-hidden="true"><i /><i /></span>
          <span className="marketing-wordmark">ReturnCatalyst</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="marketing-nav__link"
              aria-current={pathname === l.to ? 'page' : undefined}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="marketing-nav__login px-2 sm:px-3 hover:bg-transparent"
            onClick={() => nav('/login')}
          >
            Log In
          </Button>
          <Button
            size="sm"
            className="marketing-nav__demo hidden px-4 lg:inline-flex"
            onClick={() => setInquiryOpen(true)}
          >
            Request demo <span className="marketing-arrow-control"><ArrowUpRight className="h-3.5 w-3.5" /></span>
          </Button>
          <button
            ref={menuTriggerRef}
            className="marketing-nav__toggle h-11 w-11 p-2 lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="marketing-mobile-menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="marketing-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease }}
            className="marketing-nav__mobile lg:hidden overflow-hidden"
          >
            <div className="px-5 py-6 space-y-4">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => closeMenu(true)}
                  className="marketing-nav__mobile-link block w-full py-1"
                  aria-current={pathname === l.to ? 'page' : undefined}
                >
                  {l.label}
                </Link>
              ))}
              <div className="pt-5 border-t border-[#DCE5DF] flex flex-col gap-2">
                <Button
                  variant="ghost"
                  className="marketing-nav__login justify-start hover:bg-transparent"
                  onClick={() => {
                    closeMenu(true);
                    nav('/login');
                  }}
                >
                  Log In
                </Button>
                <Button
                  className="marketing-nav__demo justify-start"
                  onClick={() => {
                    closeMenu(true);
                    setInquiryOpen(true);
                  }}
                >
                  Request demo <span className="marketing-arrow-control"><ArrowUpRight className="h-3.5 w-3.5" /></span>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <DemoInquiryDialog
        open={inquiryOpen}
        onOpenChange={setInquiryOpen}
        title="Request a Demo"
        source="marketing_nav_request_demo"
      />
    </motion.nav>
  );
}
